import { EventEmitter } from "eventemitter3";
import { useCallback, useEffect } from "react";

import { type PubSubData } from "@/types";

const emitter = new EventEmitter();

export const usePublish = (initialData?: PubSubData) => {
  return (eventName: string, data?: PubSubData) => {
    emitter.emit(eventName, { ...initialData, ...data });
  };
};

export const useSubscribe = (
  eventName: string,
  listener: EventEmitter.EventListener<string, string>,
  context?: string | string[],
) => {
  const conditionalListener = useCallback(
    (data: PubSubData) => {
      const doesContextMatch = (ctx?: string) => {
        if (!context) {
          return true;
        }

        return typeof context === "object"
          ? context.some((ctx_) => ctx_ === ctx)
          : ctx === context;
      };

      return doesContextMatch(data.context) ? listener(data) : null;
    },
    [context, listener],
  );

  const unsubscribe = useCallback(() => {
    emitter.off(eventName, conditionalListener);
  }, [eventName, conditionalListener]);

  useEffect(() => {
    emitter.on(eventName, conditionalListener);
    return unsubscribe;
  }, [eventName, conditionalListener, unsubscribe]);

  return unsubscribe;
};
